import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Card,
  Checkbox,
  Grid,
  GridBreak,
  pn,
  Stack,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Select } from "components";
import {
  LkStudentDisplayFieldId,
  LkStudentServiceDisplayFieldId,
  ObjectiveReportsFilter as ObjectiveReportsFilterModel,
  ObjectiveReportsResource,
} from "generated/openApi";
import produce from "immer";

export const ObjectiveReportFilter = (props: {
  filter: ObjectiveReportsFilterModel;
  setFilter: (filter: ObjectiveReportsFilterModel) => void;
  resource: ObjectiveReportsResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource.filterProperties || [];
  const studentDisplayFields = props.resource.studentDisplayFields || [];
  const programDisplayField = studentDisplayFields.find((x) => x.id === LkStudentDisplayFieldId.Program);

  const serviceDisplayFields = props.resource.serviceDisplayFields || [];
  const serviceReferralDisplayField = serviceDisplayFields.find(
    (x) => x.id === LkStudentServiceDisplayFieldId.Referral
  );

  const schoolYearField = () => {
    const propertyName = pn<ObjectiveReportsFilterModel>("schoolYear");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={props.resource.schoolYears?.find((x) => x.id === props.filter.schoolYear)}
        options={props.resource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolYear = value?.id;
            })
          )
        }
        errors={props.validationErrors?.[propertyName]}
      />
    );
  };

  const programField = () => {
    const propertyName = pn<ObjectiveReportsFilterModel>("programId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        label={programDisplayField?.name}
        value={props.resource.programs?.find((x) => x.id === props.filter.programId)}
        options={props.resource.programs}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.programId = value?.id;
            })
          )
        }
        errors={props.validationErrors?.[propertyName]}
      />
    );
  };

  const includeServices = () => {
    const propertyName = pn<ObjectiveReportsFilterModel>("includeServices");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.filter.includeServices}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.includeServices = value;
            })
          )
        }
      />
    );
  };

  const serviceDateFrom = () => {
    const propertyName = pn<ObjectiveReportsFilterModel>("serviceDateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.serviceDateFrom}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceDateFrom = value;
            })
          )
        }
        errors={props.validationErrors?.[propertyName]}
      />
    );
  };

  const serviceDateTo = () => {
    const propertyName = pn<ObjectiveReportsFilterModel>("serviceDateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.serviceDateTo}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceDateTo = value;
            })
          )
        }
        errors={props.validationErrors?.[propertyName]}
      />
    );
  };

  const serviceReferral = () => {
    const propertyName = pn<ObjectiveReportsFilterModel>("serviceReferral");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.serviceReferral)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceReferral = value?.id;
            })
          )
        }
        errors={props.validationErrors?.[propertyName]}
      />
    );
  };

  return (
    <Card header="Filters">
      <Stack ref={breakpointsRef} spacing="lg">
        <Grid container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {schoolYearField()}
          </Grid>

          {programDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {programField()}
            </Grid>
          )}

          <GridBreak />

          <Grid item xs={smallFieldBreakpoints}>
            {includeServices()}
          </Grid>

          <GridBreak />

          {props.filter.includeServices && (
            <>
              <Grid item xs={smallFieldBreakpoints}>
                {serviceDateFrom()}
              </Grid>

              <Grid item xs={smallFieldBreakpoints}>
                {serviceDateTo()}
              </Grid>

              {serviceReferralDisplayField && (
                <Grid item xs={smallFieldBreakpoints}>
                  {serviceReferral()}
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Stack>
    </Card>
  );
};
